import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import {
  addProjectMember,
  deleteConfirmationSettingsRow,
  deleteProjectMember,
  editProject,
  getProjectMembers,
  getProjectMembersRoles,
  saveIssueWatchers,
  updateProjectMember,
} from '../../../../packages/common/api';
import FormField from '../../../../packages/common/shared-ui/src/components/FormField/FormField';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from '../../../../packages/common/shared-ui/src/icons/DeleteIcon';
import DeleteIconBig from '../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import SubmitIcon from '../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UserNewEditIcon from '../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Grid)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));

// Original class: fieldAutocomplete
const StyledFieldAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop =>
    !['multipleStyle', 'borderlessStyle'].includes(prop),
})(({ multipleStyle, borderlessStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    background: '#fff',
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(multipleStyle && {
      minHeight: '36px',
      padding: '0 65px 0 0',
    }),
    ...(borderlessStyle && {
      cursor: 'pointer',
      paddingLeft: 0,
      paddingRight: 0,
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    }),
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
      ...(multipleStyle && {
        marginLeft: '16px',
      }),
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
    '&:hover': {
      '&.MuiInputBase-root': {
        '&.MuiOutlinedInput-root': {
          backgroundColor: '#fff',
        },
      },
    },
  },
  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          padding: '8px 10px',
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      position: 'static',
      height: '10px !important',
    },
  },
}));
const ProjectMembers = () => {
  const getLocalString = useTranslation();
  const navigate = useNavigate();
  const pageParams = useParams();

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [membersOptions, setMembersOptions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let ignore = false;
    async function getMembers() {
      const response = await getProjectMembers(pageParams.projectId);
      if (!ignore) {
        return response;
      }
    }
    getMembers()
      .then(res => {
        const { data: membersData } = res;
        setMembers(membersData.filter(item => item.isChecked));
        setMembersOptions(membersData.filter(item => !item.isChecked));
        setLoading(false);
      })
      .catch(error => handleError(error.response));
    return () => {
      ignore = true;
    };
  }, [handleError, pageParams.projectId]);

  useEffect(() => {
    let ignore = false;
    async function getRoles() {
      const response = await getProjectMembersRoles(pageParams.projectId);
      if (!ignore) {
        return response;
      }
    }
    getRoles()
      .then(res => {
        const { data: rolesData } = res;
        setRoles(rolesData);
      })
      .catch(error => handleError(error.response));
    return () => {
      ignore = true;
    };
  }, [handleError, pageParams.projectId]);

  const handleAddMember = useCallback(value => {
    setMembers(members => [...members, { ...value, new: true, edit: true }]);
  }, []);
  const handleDeleteMember = useCallback(
    async member => {
      setActionLoading(true);
      if (member.new) {
        const newArray = members.filter(item => item.userId !== member.userId);
        setMembers(newArray);
        setActionLoading(false);
        return;
      }
      try {
        const { data: response } = await deleteProjectMember(
          pageParams.projectId,
          member.memberId,
        );
        if (response) {
          setActionLoading(false);
          setMembers(response.filter(item => item.isChecked));
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [members, pageParams.projectId],
  );
  const handleSaveMember = useCallback(
    async member => {
      setActionLoading(true);
      let data = {
        userId: member.userId,
        roles: member.roles.map(item => item.roleId),
      };
      try {
        let response;
        if (member.new) {
          response = await addProjectMember(pageParams.projectId, data);
        } else {
          delete data.userId;
          response = await updateProjectMember(
            pageParams.projectId,
            member.memberId,
            data,
          );
        }
        if (response) {
          const { data: members } = response;
          setActionLoading(false);
          setMembers(members.filter(item => item.isChecked));
          setMembersOptions(members.filter(item => !item.isChecked));
        }
      } catch (error) {
        console.error('ERROR WITH SAVE', error);
      }
    },
    [pageParams.projectId],
  );
  const handleEditMember = useCallback(
    member => {
      const index = members.findIndex(
        item => item.memberId === member.memberId,
      );
      const updatedObject = { ...members[index], edit: true };
      setMembers([
        ...members.slice(0, index),
        updatedObject,
        ...members.slice(index + 1),
      ]);
    },
    [members],
  );

  const handleRoleSelect = useCallback(
    (value, member) => {
      const pos = members.map(e => e.userId).indexOf(member.userId);
      const updatedObject = {
        ...member,
        roles: value,
      };
      const newMembers = [
        ...members.slice(0, pos),
        updatedObject,
        ...members.slice(pos + 1),
      ];
      setMembers(newMembers);
    },
    [members],
  );

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <StyledBlockWrapper
          container
          direction={'column'}
          sx={{
            padding: '24px 16px 16px 32px',
            position: 'relative',
          }}
        >
          {actionLoading && (
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                zIndex: 99,
                left: 0,
                top: 0,
                position: 'absolute',
              }}
            >
              <CircularProgress color='secondary' />
            </Grid>
          )}
          <Typography
            variant='h3'
            sx={{
              color: '#082253',
              fontWeight: 600,
              marginBottom: '24px',
            }}
          >
            {getLocalString(
              'ProjectCreateEdit',
              'sections',
              'members',
              'title',
            )}
          </Typography>
          <Grid container>
            {members.length > 0 &&
              members.map((item, index) => {
                return (
                  <Grid
                    key={index}
                    container
                    sx={{
                      flexWrap: 'nowrap',
                      width: '100%',
                      alignItems: 'center',
                      marginTop: index > 0 ? '16px' : 0,
                    }}
                  >
                    <Grid
                      sx={{
                        minWidth: '215px',
                        paddingTop: item.new ? '8px' : 0,
                      }}
                    >
                      <Typography
                        variant='h5'
                        sx={{
                          color: '#212346',
                        }}
                      >
                        {item.userName}
                      </Typography>
                    </Grid>
                    <Grid
                      sx={{
                        flex: 1,
                        paddingLeft: '16px',
                      }}
                    >
                      {item.edit ? (
                        <StyledFieldAutocomplete
                          multipleStyle={true}
                          borderlessStyle={true}
                          forcePopupIcon={false}
                          multiple
                          disableClearable
                          //open={true}
                          onChange={(event, newValue) =>
                            handleRoleSelect(newValue, item)
                          }
                          options={roles}
                          sx={{ width: '100%' }}
                          disableCloseOnSelect
                          disablePortal
                          value={item.roles}
                          isOptionEqualToValue={(option, value) =>
                            option.roleId === value.roleId
                          }
                          getOptionLabel={option => option.roleName || ''}
                          getOptionDisabled={option => {
                            let disabled = false;
                            const currentOption = item.roles.find(
                              item => item.roleId === option.roleId,
                            );
                            currentOption !== undefined &&
                              currentOption.isLocked &&
                              (disabled = true);
                            return disabled;
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.roleId}>
                              <Checkbox
                                icon=<CheckboxIcon
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                checkedIcon=<CheckboxIconChecked
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.roleName}
                            </li>
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <StyledChip
                                {...getTagProps({ index })}
                                disabled={option.isLocked}
                                key={index}
                                deleteIcon={
                                  <DeleteIcon
                                    width={10}
                                    height={10}
                                    viewBox='0 0 10 10'
                                  />
                                }
                                variant='outlined'
                                label={option.roleName}
                              />
                            ))
                          }
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={getLocalString('labels', 'addRole')}
                            />
                          )}
                        />
                      ) : (
                        <Grid
                          container
                          sx={{
                            flexDirection: 'column',
                          }}
                        >
                          {item.roles.map((item, index) => {
                            return (
                              <Box
                                key={index}
                                sx={{
                                  marginTop: index > 0 ? '5px' : 0,
                                }}
                              >
                                <Typography
                                  variant='h5'
                                  sx={{
                                    color: '#212346',
                                  }}
                                >
                                  {item.roleName}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Grid>
                      )}
                    </Grid>
                    <Grid
                      sx={{
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Grid
                        container
                        sx={{
                          marginLeft: '16px',
                        }}
                      >
                        <Grid>
                          {item.edit ? (
                            <IconButton
                              onClick={() => handleSaveMember(item)}
                              sx={{ padding: '8px', height: '100%' }}
                            >
                              <SubmitIcon width={'16px'} />
                            </IconButton>
                          ) : (
                            <IconButton
                              onClick={() => handleEditMember(item)}
                              sx={{ padding: '8px', height: '100%' }}
                            >
                              <UserNewEditIcon
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                              />
                            </IconButton>
                          )}
                        </Grid>
                        {!item.isUnremovable && (
                          <Grid>
                            <IconButton
                              onClick={() => handleDeleteMember(item)}
                              sx={{ padding: '8px', height: '100%' }}
                            >
                              <DeleteIconBig />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
          </Grid>
          <Box>
            <StyledFieldAutocomplete
              borderlessStyle={true}
              noOptionsText={getLocalString('labels', 'notFound')}
              disabled={members.filter(item => item.new).length > 0}
              onChange={(event, newValue) => handleAddMember(newValue)}
              disablePortal
              options={membersOptions}
              //sx={{ width: 253 }}
              disableClearable
              renderOption={(props, option) => {
                return (
                  <MenuItem
                    {...props}
                    key={option.userId}
                    value={option.userId}
                    //selected={field.valueId && option.valueId.toString() === field.valueId.toString()}
                  >
                    {option.userName}
                  </MenuItem>
                );
              }}
              value={null}
              //isOptionEqualToValue={(option, value) => option.userId === value.userId}
              getOptionLabel={option => option.userName || ''}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={getLocalString('labels', 'addMember')}
                />
              )}
              forcePopupIcon={false}
              slots={{
                popper: props => (
                  <Popper {...props} placement='bottom-start'></Popper>
                ),
              }}
            />
          </Box>
        </StyledBlockWrapper>
      )}
    </>
  );
};

export default ProjectMembers;
